import { useCallback, useEffect, useMemo, useState } from "react";
import { Input, Modal, Switch, Table, message } from "antd";
import type { TableColumnsType } from "antd";
import { ChevronDown, ChevronUp, Pencil, Plus, Trash2 } from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import LoadingOverlay from "components/loading-overlay/loading-overlay";

import { useGetPlanReviewCategoriesAdminQuery } from "graphql/queries/get-plan-review-categories-admin.generated";
import { useInsertPlanReviewCategoryMutation } from "graphql/mutations/insert-plan-review-category.generated";
import { useUpdatePlanReviewCategoryMutation } from "graphql/mutations/update-plan-review-category.generated";
import { useDeletePlanReviewCategoryMutation } from "graphql/mutations/delete-plan-review-category.generated";
import { useReorderPlanReviewCategoriesMutation } from "graphql/mutations/reorder-plan-review-categories.generated";

import { usePlanReviewJurisdiction } from "hooks/usePlanReviewJurisdiction";
import { usePlanReviewSettingsAccess } from "hooks/usePlanReviewSettingsAccess";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildScreenDescription } from "utils/ollie-screen-description";

type Category = NonNullable<
	ReturnType<typeof useGetPlanReviewCategoriesAdminQuery>["data"]
>["plan_review_categories"][number];

interface FormValues {
	id?: string;
	code: string;
	name: string;
	ai_guidance: string;
	position: number;
	active: boolean;
}

const EMPTY: FormValues = { code: "", name: "", ai_guidance: "", position: 0, active: true };

// The code is a machine identifier, so it is derived from the name rather than asked
// for: an AHJ manager naming a category should not have to think about slugs.
const toCode = (name: string): string =>
	name
		.trim()
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "");

/**
 * The Review Categories of one jurisdiction: how its rules are filed and how its
 * reports are grouped.
 *
 * The guidance is not decoration — it is pasted into the extraction and analysis
 * prompts, so it is what decides where the AI files a rule. A category with a name and
 * no guidance leaves the model guessing from the name alone.
 */
const PlanReviewCategories = () => {
	const { jurisdictionId, picker, emptyReason } = usePlanReviewJurisdiction();
	// Tells Ollie what the reader is looking at, and for which authority.
	useDescribeScreenToOllie(
		buildScreenDescription({
			screen:
				"The review categories screen: the areas this authority files its plan review findings under. The reader maintains them here.",
			jurisdictionId
		})
	);

	const { canManage, blockChange, notice } = usePlanReviewSettingsAccess();
	// Appended to every control that writes: it reads as unavailable but still answers.
	const lockedClass = canManage ? "" : " prc-locked";

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [values, setValues] = useState<FormValues>(EMPTY);
	const [touched, setTouched] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const {
		data,
		loading,
		error: loadError,
		refetch
	} = useGetPlanReviewCategoriesAdminQuery({
		skip: !jurisdictionId,
		variables: { jurisdictionId },
		fetchPolicy: "cache-and-network"
	});

	const [insertCategory] = useInsertPlanReviewCategoryMutation();
	const [updateCategory] = useUpdatePlanReviewCategoryMutation();
	const [deleteCategory] = useDeletePlanReviewCategoryMutation();
	const [reorderCategories] = useReorderPlanReviewCategoriesMutation();

	const categories = useMemo(() => data?.plan_review_categories ?? [], [data]);

	useEffect(() => {
		if (isModalOpen) setTouched(false);
	}, [isModalOpen]);

	// Renumbers the whole list 1..N from the order given, in one mutation.
	const persistOrder = useCallback(
		async (ordered: Category[]) => {
			const updates = ordered
				.map((category, index) => ({ category, position: index + 1 }))
				.filter(({ category, position }) => category.sort_order !== position)
				.map(({ category, position }) => ({
					where: { id: { _eq: category.id } },
					_set: { sort_order: position }
				}));
			if (updates.length > 0) await reorderCategories({ variables: { updates } });
		},
		[reorderCategories]
	);

	const move = useCallback(
		async (category: Category, direction: -1 | 1) => {
			if (!canManage) return blockChange();
			const current = categories.findIndex(c => c.id === category.id);
			const target = current + direction;
			if (current < 0 || target < 0 || target >= categories.length) return;
			const reordered = [...categories];
			[reordered[current], reordered[target]] = [reordered[target], reordered[current]];
			try {
				await persistOrder(reordered);
				await refetch();
			} catch (moveError) {
				console.error("Review category reorder failed:", moveError);
				message.error("We couldn't reorder the categories. Please try again.");
			}
		},
		[categories, persistOrder, refetch, canManage, blockChange]
	);

	const openCreate = useCallback(() => {
		if (!canManage) return blockChange();
		// A new category goes last unless the manager says otherwise.
		setValues({ ...EMPTY, position: categories.length + 1 });
		setError(null);
		setIsModalOpen(true);
	}, [categories.length, canManage, blockChange]);

	const openEdit = useCallback(
		(category: Category) => {
			if (!canManage) return blockChange();
			setValues({
				id: category.id,
				code: category.code,
				name: category.name,
				ai_guidance: category.ai_guidance ?? "",
				position: category.sort_order,
				active: category.active
			});
			setError(null);
			setIsModalOpen(true);
		},
		[canManage, blockChange]
	);

	// Two categories with the same name are indistinguishable in the report, and the
	// model could not tell them apart either.
	const duplicateName = useMemo(() => {
		const name = values.name.trim().toLowerCase();
		if (!name) return false;
		return categories.some(c => c.id !== values.id && c.name.trim().toLowerCase() === name);
	}, [categories, values.id, values.name]);

	const handleSave = useCallback(async () => {
		setTouched(true);
		if (!values.name.trim() || duplicateName) return;
		setIsSaving(true);
		setError(null);
		try {
			const fields = {
				name: values.name.trim(),
				ai_guidance: values.ai_guidance.trim() || null,
				active: values.active
			};

			let savedId = values.id;
			if (values.id) {
				await updateCategory({ variables: { id: values.id, set: fields } });
			} else {
				const created = await insertCategory({
					variables: {
						object: {
							...fields,
							code: values.code.trim() || toCode(values.name),
							jurisdiction_id: jurisdictionId,
							// Parked at the end; the renumber below puts it where asked.
							sort_order: categories.length + 1
						}
					}
				});
				savedId = created.data?.insert_plan_review_categories_one?.id;
			}

			// Rebuild the order with this category at the requested position, then
			// renumber everything 1..N so no two rows can end up sharing a number.
			const { data: fresh } = await refetch();
			const rows = [...(fresh?.plan_review_categories ?? [])];
			const moving = rows.findIndex(c => c.id === savedId);
			if (moving >= 0) {
				const [row] = rows.splice(moving, 1);
				const target = Math.min(Math.max(values.position, 1), rows.length + 1) - 1;
				rows.splice(target, 0, row);
				await persistOrder(rows);
				await refetch();
			}

			setIsModalOpen(false);
			message.success(values.id ? "Category updated." : "Category created.");
		} catch (saveError) {
			console.error("Review category save failed:", saveError);
			const detail = saveError instanceof Error ? saveError.message : "";
			setError(
				detail.includes("name_uniq")
					? "Another category in your jurisdiction already uses that name."
					: detail.includes("uniq")
						? "Another category in your jurisdiction already uses that code."
						: `We couldn't save the category. ${detail}`.trim()
			);
		} finally {
			setIsSaving(false);
		}
	}, [values, duplicateName, updateCategory, insertCategory, jurisdictionId, categories.length, refetch, persistOrder]);

	const handleDelete = useCallback(
		async (category: Category) => {
			if (!canManage) return blockChange();
			try {
				const result = await deleteCategory({ variables: { id: category.id } });
				// A row the permission filter excludes comes back as null with no error,
				// so success has to be read from the payload and not from "it didn't throw".
				if (!result.data?.delete_plan_review_categories_by_pk) {
					message.error("We couldn't delete that category.");
					return;
				}
				const { data: fresh } = await refetch();
				await persistOrder([...(fresh?.plan_review_categories ?? [])]);
				await refetch();
				message.success("Category deleted.");
			} catch (deleteError) {
				console.error("Review category delete failed:", deleteError);
				const detail = deleteError instanceof Error ? deleteError.message : "";
				message.error(
					detail.toLowerCase().includes("foreign key")
						? "This category still has rules filed under it. Move them or deactivate it instead."
						: "We couldn't delete the category. Please try again."
				);
			}
		},
		[deleteCategory, refetch, persistOrder, canManage, blockChange]
	);

	const columns: TableColumnsType<Category> = useMemo(
		() => [
			{
				title: "Order",
				key: "sort_order",
				width: 96,
				render: (_, category, index) => (
					<div className="prc-order-cell">
						<span className="prc-order-number">{category.sort_order}</span>
						<div className="prc-order-buttons">
							<button
								type="button"
								className={`prc-icon-btn prc-icon-btn-tiny${lockedClass}`}
								title="Move up"
								aria-disabled={!canManage}
								disabled={index === 0}
								onClick={() => move(category, -1)}
							>
								<ChevronUp size={13} />
							</button>
							<button
								type="button"
								className={`prc-icon-btn prc-icon-btn-tiny${lockedClass}`}
								title="Move down"
								aria-disabled={!canManage}
								disabled={index === categories.length - 1}
								onClick={() => move(category, 1)}
							>
								<ChevronDown size={13} />
							</button>
						</div>
					</div>
				)
			},
			{
				title: "Name",
				key: "name",
				width: 280,
				render: (_, category) => (
					<div>
						<p className="prc-cell-title">{category.name}</p>
						<span className="prc-code">{category.code}</span>
					</div>
				)
			},
			{
				title: "What it covers",
				dataIndex: "ai_guidance",
				key: "ai_guidance",
				render: (guidance: string | null) => (
					<p className="prc-guidance-cell">{guidance || "No guidance — the AI files by the name alone."}</p>
				)
			},
			{
				title: "Rules",
				key: "rules",
				width: 90,
				render: (_, category) => category.plan_review_requirements_aggregate.aggregate?.count ?? 0
			},
			{
				title: "",
				key: "actions",
				width: 100,
				render: (_, category) => (
					<div className="prc-cell-actions">
						<button
							type="button"
							className={`prc-icon-btn${lockedClass}`}
							title="Edit"
							aria-disabled={!canManage}
							onClick={() => openEdit(category)}
						>
							<Pencil size={15} />
						</button>
						<button
							type="button"
							className={`prc-icon-btn${lockedClass}`}
							title="Delete"
							aria-disabled={!canManage}
							onClick={() => handleDelete(category)}
						>
							<Trash2 size={15} />
						</button>
					</div>
				)
			}
		],
		[openEdit, handleDelete, move, categories.length, canManage, lockedClass]
	);

	return (
		<>
			<OllieLayout>
				<div className="prc-page">
					<div className="prc-shell">
						<header className="prc-header">
							<div>
								<h1 className="prc-title">Review Categories</h1>
								<p className="prc-subtitle">
									How your rules are filed and how your reports are grouped. The order here is the order the report
									reads in.
								</p>
							</div>
							<div className="prc-header-actions">
								<button
									className={`prc-btn prc-btn-primary${lockedClass}`}
									onClick={openCreate}
									aria-disabled={!canManage}
									disabled={!jurisdictionId}
								>
									<Plus size={16} />
									Add category
								</button>
							</div>
						</header>

						<div className="prc-header">{picker}</div>

						{notice}

						{loadError ? (
							<div className="prc-empty">
								<p className="prc-empty-title">We couldn&apos;t load the categories</p>
								<p>{loadError.message}</p>
							</div>
						) : emptyReason ? (
							<div className="prc-empty">
								<p className="prc-empty-title">{emptyReason.title}</p>
								<p>{emptyReason.detail}</p>
							</div>
						) : !loading && categories.length === 0 ? (
							<div className="prc-empty">
								<p className="prc-empty-title">No review categories yet</p>
								<p>Add the sections your reports are grouped by, so rules have somewhere to be filed.</p>
							</div>
						) : (
							<Table
								className="prc-table"
								rowKey="id"
								columns={columns}
								dataSource={categories}
								loading={loading && categories.length === 0}
								pagination={false}
								rowClassName={category => (category.active ? "" : "prc-row-inactive")}
							/>
						)}
					</div>
				</div>
			</OllieLayout>

			<Modal
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				footer={null}
				closable={false}
				width={560}
				className="prc-modal"
			>
				<h2 className="prc-modal-title">{values.id ? "Edit category" : "Add category"}</h2>
				<p className="prc-modal-sub">Only your jurisdiction sees and uses it.</p>

				<div className="prc-form">
					<div className="prc-form-field prc-form-field-full">
						<label className="prc-label">Name</label>
						<Input
							className="prc-control"
							placeholder="e.g. Calculations & Documentation"
							value={values.name}
							onChange={e => setValues(v => ({ ...v, name: e.target.value }))}
							status={touched && (!values.name.trim() || duplicateName) ? "error" : undefined}
						/>
						{touched && duplicateName ? (
							<p className="prc-error">You already have a category with this name.</p>
						) : (
							<p className="prc-hint">The heading this group of findings appears under in the report.</p>
						)}
					</div>

					<div className="prc-form-field prc-form-field-full">
						<label className="prc-label">What it covers</label>
						<Input.TextArea
							className="prc-control"
							rows={4}
							placeholder="e.g. Anything the applicant must compute, test or report: battery and voltage-drop calculations, fire flow tests, hydraulic calculations, commissioning reports, narratives."
							value={values.ai_guidance}
							onChange={e => setValues(v => ({ ...v, ai_guidance: e.target.value }))}
						/>
						<p className="prc-hint">
							This goes into the AI prompt as-is. Describe what belongs here so a rule is filed under it instead of a
							neighbouring category.
						</p>
					</div>

					<div className="prc-form-field">
						<label className="prc-label">Position</label>
						<Input
							className="prc-control"
							type="number"
							min={1}
							max={values.id ? categories.length : categories.length + 1}
							value={values.position}
							onChange={e => setValues(v => ({ ...v, position: Number(e.target.value) || 1 }))}
						/>
						<p className="prc-hint">Where the section sits in the report. The others shift to make room.</p>
					</div>

					{values.id ? (
						<div className="prc-form-field prc-form-field-full">
							<div>
								<Switch
									checked={values.active}
									onChange={checked => setValues(v => ({ ...v, active: checked }))}
									className="prc-switch"
								/>
								<span className="prc-hint">
									{" "}
									Inactive categories stay on the rules already filed under them but are no longer offered.
								</span>
							</div>
						</div>
					) : null}
				</div>

				{error ? <p className="prc-error">{error}</p> : null}

				<div className="prc-modal-actions">
					<button type="button" className="prc-btn" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
						Cancel
					</button>
					<button type="button" className="prc-btn prc-btn-primary" onClick={handleSave} disabled={isSaving}>
						{isSaving ? "Saving…" : "Save category"}
					</button>
				</div>
			</Modal>

			<LoadingOverlay isLoading={isSaving} tip="Saving…" />
		</>
	);
};

export default PlanReviewCategories;
